import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * StripeConfigProvider — fetches `/api/config/stripe` once at app boot
 * and memoizes the publishable key. The key is public by design
 * (Stripe `pk_test_...` / `pk_live_...`), so we expose it to any
 * component that needs it.
 *
 * Phase 3b: this context is the single source of truth for the
 * frontend's Stripe publishable key. We do NOT call `loadStripe()`
 * eagerly — Checkout sessions are server-driven via a redirect to
 * `session.url`, so we don't need a `Stripe` client in the browser.
 * The publishable key is kept here for future flows (Payment
 * Elements, etc.) that DO need it.
 */
const StripeConfigContext = createContext({
  publishableKey: null,
  stubMode: false,
  loading: true,
  error: null,
});

export function StripeConfigProvider({ children }) {
  const [state, setState] = useState({
    publishableKey: null,
    stubMode: false,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/config/stripe");
        if (cancelled) return;
        setState({
          publishableKey: data?.publishable_key || null,
          // Phase 3.6 — additive boolean. Backend (`stripe_checkout.py
          // get_stripe_config`) reads `os.environ["PP_STRIPE_STUB"] == "1"`
          // and surfaces it here. Coerce to strict boolean so consumers
          // can branch with `if (stubMode)` without `=== true` ceremony.
          stubMode: data?.stub_mode === true,
          loading: false,
          error: null,
        });
      } catch (e) {
        if (cancelled) return;
        setState({
          publishableKey: null,
          // On error we DEFAULT TO false (NOT stub mode) so the FE never
          // accidentally shows the deprecated mock-pay button when we can't
          // confirm we're in stub mode. Fail closed for trust-critical UX.
          stubMode: false,
          loading: false,
          error: e?.response?.data?.detail || e?.message || "stripe_pk_missing",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <StripeConfigContext.Provider value={state}>
      {children}
    </StripeConfigContext.Provider>
  );
}

export function useStripeConfig() {
  return useContext(StripeConfigContext);
}
